/* eslint-disable no-console */
// console statements are wrapped in options; idb is not visible normally
import { ActionReducer, INIT, UPDATE } from '@ngrx/store';
import deepmerge from '@bundled-es-modules/deepmerge';
import { NgrxStoreIdbStoreProvider } from './ngrx-store-idb-store.provider';
import {
  rehydrateAction,
  RehydrateActionPayload,
  rehydrateErrorAction,
  rehydrateInitAction,
} from './ngrx-store-idb.actions';
import { NgrxStoreIdbFlushQueue } from './ngrx-store-idb.flush';
import { Keys, NgrxStoreIdbOptions } from './ngrx-store-idb.options';
import { NgrxStoreIdbService } from './ngrx-store-idb.service';

/**
 * Default marshaller saves the whole store state
 */
export const defaultMarshaller = (state: any) => state;

// Default merge strategy is a full deep merge.
export const defaultUnmarshaller = (state: any, rehydratedState: any) => {
  // Leave options here to provide an example for custom unmarshallers
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const overwriteMerge = (destinationArray: any, sourceArray: any, options: any) => sourceArray;
  const options: deepmerge.Options = {
    arrayMerge: overwriteMerge,
  };

  const mergedState: any = deepmerge(state, rehydratedState, options);

  // Clear loading status
  if (mergedState?.entityCache) {
    for (const entityName in mergedState.entityCache) {
      mergedState.entityCache[entityName].loading = false;
    }
  }

  return mergedState;
};

export const DEFAULT_OPTS: NgrxStoreIdbOptions = {
  rehydrate: true,
  syncCondition: null,
  keys: null,
  unmarshaller: defaultUnmarshaller,
  marshaller: defaultMarshaller,
  perEntryKeys: [],
  flush: {
    debounceMs: 1000,
    maxWaitMs: 5000,
  },
  debugInfo: true,
  idb: {
    dbNameForTenant: (tenantId: string) => `NgrxStoreIdb-${tenantId}`,
    storeName: 'Store',
    bootstrapTenantId: () => null,
  },
  concurrency: {
    allowed: false,
    refreshRate: 5000,
    trackKey: 'ConcurrencyTimestamp',
    failInitialisationIfNoLock: false,
  },
};

/**
 * Restricts a rehydrated record to the currently configured top-level keys.
 * The record on disk may predate the current `keys` configuration (e.g. a
 * slice that used to be persisted here but has since moved to another storage
 * mechanism); merging such stale slices back into the store would resurrect
 * state the application no longer expects from IndexedDB. Reads must therefore
 * be filtered symmetrically with `keysMarshaller` on the write side.
 */
const keysUnmarshallerFilter = (rehydratedState: any, keys: Keys) => {
  if (!rehydratedState) {
    return rehydratedState;
  }

  const res = {};
  keys.forEach((key) => {
    const name = typeof key === 'object' ? Object.keys(key)[0] : key as string;
    if (rehydratedState[name] != null) {
      res[name] = rehydratedState[name];
    }
  });

  return res;
};

/**
 * Marks the slices this action changed as dirty on the flush queue. The
 * expensive work (serialisation, IndexedDB write) happens later, coalesced
 * off the dispatch call stack - see NgrxStoreIdbFlushQueue.
 */
const syncStateUpdate = (
  state,
  action,
  opts: NgrxStoreIdbOptions,
  flushQueue: NgrxStoreIdbFlushQueue,
) => {
  if (opts.syncCondition) {
    try {
      if (opts.syncCondition(state, action) !== true) {
        if (opts.debugInfo) {
          console.debug('NgrxStoreIdb: State will not be persisted. syncCondition is false');
        }
        return;
      }
    } catch (e) {
      // Treat TypeError as do not sync
      if (e instanceof TypeError) {
        if (opts.debugInfo) {
          console.debug('NgrxStoreIdb: State will not be persisted. syncCondition has error', e);
        }
        return;
      }
      console.error('NgrxStoreIdb: syncCondition raised error', e);
      throw e;
    }
  }

  flushQueue.update(state, action);
};

/**
 * This is the main factory that creates our metareducer.
 */

export const metaReducerFactoryWithOptions = (
  options: NgrxStoreIdbOptions,
  storeProvider: NgrxStoreIdbStoreProvider,
  service: NgrxStoreIdbService,
) => {
  let rehydratedState = null;
  const flushQueue = new NgrxStoreIdbFlushQueue(options, storeProvider, service);
  return (reducer: ActionReducer<any>) => (state: any, action) => {
    let nextState: any;

    if (options.debugInfo) {
      console.group('NgrxStoreIdb: metareducer', state, action);
    }

    // If we are processing rehydrateAction then save rehydrated state (for later use).
    // There is no other reducer for this action.
    if (action.type === rehydrateAction.type) {
      const payload = action as RehydrateActionPayload;
      rehydratedState = options.keys
        ? keysUnmarshallerFilter(payload.rehydratedState, options.keys)
        : payload.rehydratedState || {};
      rehydratedState = rehydratedState || {};
      if (!payload.rehydratedState) {
        if (options.debugInfo) {
          console.debug('NgrxStoreIdb: Rehydrated state is empty - nothing to rehydrate.');
          console.groupEnd();
        }
        return state;
      }
    }

    // If action is rehydrateAction (i.e. initial rehydratation)
    // then merge the store state with the rehydrated state
    if (action.type === rehydrateAction.type) {
      nextState = options.unmarshaller(state, rehydratedState);
      // The merged state is now what the application runs on; treat it as
      // already persisted so only subsequent changes are written. (The read
      // path re-sanitises on every boot, so the on-disk copy staying at its
      // pre-merge value is fine.)
      flushQueue.seed(nextState, options.keys);
      if (options.debugInfo) {
        console.debug('NgrxStoreIdb: After rehydrating current state', nextState);
      }
    } else {
      // Run normal reducer for this action
      nextState = reducer(state, action);
    }

    // If action is UPDATE then rehydrate feature slices just created (when lazy module store loads)
    if (action.type === UPDATE && action.features && rehydratedState) {
      const rehydratedStateCopy = {};
      for (const feature of action.features) {
        if (rehydratedState[feature]) {
          rehydratedStateCopy[feature] = rehydratedState[feature];
        }
      }
      nextState = options.unmarshaller(nextState, rehydratedStateCopy);
      if (options.keys) {
        const featureKeys: Keys = options.keys.filter(key =>
          action.features.includes(typeof key === 'object' ? Object.keys(key)[0] : key),
        );
        flushQueue.seed(nextState, featureKeys);
      }
      if (options.debugInfo) {
        console.debug('NgrxStoreIdb: After rehydrating current state', nextState);
      }
    }

    if (
      action.type !== INIT &&
      action.type !== UPDATE &&
      action.type !== rehydrateInitAction.type &&
      action.type !== rehydrateAction.type &&
      action.type !== rehydrateErrorAction.type &&
      // If rehydrating is requested then don't sync until the saved state was loaded first
      (rehydratedState || !options.rehydrate)
    ) {
      if (options.debugInfo) {
        console.debug('NgrxStoreIdb: Try to persist state into IndexedDB', nextState, action);
      }
      syncStateUpdate(nextState, action, options, flushQueue);
    }

    if (options.debugInfo) {
      console.groupEnd();
    }

    return nextState;
  };
};

export const optionsFactory = (options: Partial<NgrxStoreIdbOptions>) => {
  if (options.keys && (options.unmarshaller || options.marshaller)) {
    throw new Error('NgrxStoreIdb: define keys or unmarshaller+marshaller but not both!');
  }
  if (!!options.marshaller !== !!options.unmarshaller) {
    throw new Error('NgrxStoreIdb: define unmarshaller and marshaller!');
  }
  const opts = deepmerge(DEFAULT_OPTS, options);
  if (opts.debugInfo) {
    console.info('NgrxStoreIdbModule: Using the following options', {
      ...opts,
      marshaller:
        opts.marshaller === defaultMarshaller ? 'default marshaller' : 'custom marshaller',
      unmarshaller:
        opts.unmarshaller === defaultUnmarshaller ? 'default unmarshaller' : 'custom unmarshaller',
    });
  }
  return opts;
};

export const ngrxStoreIdbServiceInitializer = (
  opts: NgrxStoreIdbOptions,
  service: NgrxStoreIdbService,
  storeProvider: NgrxStoreIdbStoreProvider,
) => {
  return (): Promise<boolean> => {
    // When the tenant is unknown at bootstrap the store is created lazily
    // after app init (the tenant resolves via API calls that need the app
    // running) - the lock outcome can't be awaited here without deadlock.
    if (storeProvider.bootstrapTenantId == null) {
      return Promise.resolve(true);
    }
    // The election reads from IndexedDB, which can queue indefinitely when a
    // deleteDatabase is pending in another tab. Never let that block app
    // startup: proceed without the lock (the tab simply won't persist state
    // until the election eventually completes).
    const electionTimeout = new Promise<'timeout'>((resolve) => {
      setTimeout(() => resolve('timeout'), opts.concurrency.refreshRate);
    });
    return Promise.race([ service.onLockAcquired().toPromise(), electionTimeout ])
      .then(
        hasLock =>
          new Promise((resolve, reject) => {
            if (hasLock === 'timeout') {
              console.warn(
                'NgrxStoreIdb: Lock election did not complete in time. Starting without persistence.',
              );
              resolve(true);
            } else if (hasLock || !opts.concurrency.failInitialisationIfNoLock) {
              resolve(true);
            } else {
              reject('Can not acquire master lock. Another tab/window is open?');
            }
          }),
      );
  };
};
